/* eslint-disable security/detect-object-injection */
export type GeneralEventTypes = {
  [EventName: string]: unknown[];
};

type EventListener<
  EventTypes extends GeneralEventTypes,
  EventName extends keyof EventTypes,
> = (...args: EventTypes[EventName]) => void;

type EventMap<EventTypes extends GeneralEventTypes> = {
  [EventName in keyof EventTypes]: Set<EventListener<EventTypes, EventName>>;
};

class EventEmitter<EventTypes extends GeneralEventTypes> {
  private listeners = {} as EventMap<EventTypes>;

  public on<EventName extends keyof EventTypes>(
    event: EventName,
    listener: EventListener<EventTypes, EventName>,
    options?: {once?: boolean}
  ): () => void {
    if (!this.listeners[event]) {
      this.listeners[event] = new Set();
    }

    if (options?.once) {
      const onceWrapper: EventListener<EventTypes, EventName> = (...args) => {
        this.un(event, onceWrapper);
        listener(...args);
      };
      this.listeners?.[event]?.add?.(onceWrapper);
      return () => this.un(event, onceWrapper);
    }

    this.listeners?.[event]?.add?.(listener);
    return () => this.un(event, listener);
  }

  public un<EventName extends keyof EventTypes>(
    event: EventName,
    listener: EventListener<EventTypes, EventName>
  ): void {
    this.listeners?.[event]?.delete?.(listener);
  }

  public once<EventName extends keyof EventTypes>(
    event: EventName,
    listener: EventListener<EventTypes, EventName>
  ): () => void {
    return this.on(event, listener, {once: true});
  }

  public unAll(): void {
    this.listeners = {} as EventMap<EventTypes>;
  }

  protected emit<EventName extends keyof EventTypes>(
    eventName: EventName,
    ...args: EventTypes[EventName]
  ): void {
    if (this.listeners[eventName]) {
      this.listeners[eventName].forEach((listener) => listener(...args));
    }
  }
}

export default EventEmitter;
